import { Close, InfoOutlined } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { TableRow as ITableRow, JsonModalProps } from "./types";

import { useTheme } from "@mui/material/styles";

const JsonModal = ({ open, onClose, data, title }: JsonModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Convert JSON object to array of key-value pairs for table display
  const jsonToTableData = (json: any): ITableRow[] => {
    if (!json || typeof json !== "object") return [];
    return Object.entries(json).map(([key, value]) => ({
      key,
      value:
        typeof value === "object"
          ? JSON.stringify(value, null, 2)
          : String(value),
    }));
  };

  const tableData = jsonToTableData(data);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlined color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {title || "JSON Data"}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          data-attr="assistant-actions:btn:close-json-modal"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: "30%",
                    backgroundColor: theme.palette.grey[50],
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Key
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: "70%",
                    backgroundColor: theme.palette.grey[50],
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(even)": {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                    }}
                  >
                    {row.key}
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        py: 0.5,
                        px: 1,
                        fontSize: "0.8125rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 0.5,
                        border: `1px solid ${theme.palette.divider}`,
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      {row.value}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {tableData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" variant="body2">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default JsonModal;
